import React from "react";
import { Link } from "react-router-dom";
import {
    PartyPopper, Users, Package, Calendar, MapPin, ShoppingBasket,
    Truck, IndianRupee, Clock, Apple, Leaf, Scale, Sparkles,
    Globe, UserCheck, BadgeCheck, TrendingUp, ShieldCheck, FileText,
    Banknote, Briefcase, ExternalLink, Check,
} from "lucide-react";
import { isServiceCategory } from "./ServicesMetaForm";
import { useAuth } from "../lib/auth";

const REGULATOR_LOOKUP = {
    "mutual-fund-distributor": {
        regulator: "AMFI",
        url: "https://www.amfiindia.com/locate-distributor",
        verifyType: "amfi-listing",
        verifiedFlag: "amfi_verified",
        badgeLabel: "AMFI-Verified Distributor",
    },
    "insurance-distributor": {
        regulator: "IRDAI",
        url: "https://agencyportal.irdai.gov.in/PublicAccess/AgentLocator.aspx",
        verifyType: "irdai-listing",
        verifiedFlag: "irdai_verified",
        badgeLabel: "IRDAI-Verified Agent",
    },
};

function VerificationStrip({ listing }) {
    const cfg = REGULATOR_LOOKUP[listing.category];
    const { user } = useAuth();
    if (!cfg) return null;
    const isVerified = !!listing?.meta?.[cfg.verifiedFlag];
    const isOwner = user?.id === listing?.user_id;
    return (
        <div className="mt-4 border-t border-aduku-border pt-4 flex flex-wrap items-center gap-2">
            {isVerified ? (
                <span data-testid={`${cfg.verifyType}-verified-badge`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-bold uppercase tracking-wider">
                    <Check className="w-3.5 h-3.5" /> {cfg.badgeLabel}
                </span>
            ) : isOwner ? (
                <Link
                    to={`/account/verify?type=${cfg.verifyType}&listing=${listing.id}`}
                    data-testid={`${cfg.verifyType}-request-link`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-aduku-yellow text-aduku-navy text-xs font-bold hover:bg-aduku-yellow-600"
                >
                    <BadgeCheck className="w-3.5 h-3.5" /> Get {cfg.regulator}-Verified badge
                </Link>
            ) : null}
            <a
                href={cfg.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`${cfg.verifyType}-regulator-link`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-aduku-navy/20 text-aduku-navy text-xs font-semibold hover:border-aduku-navy"
            >
                <ExternalLink className="w-3.5 h-3.5" /> Self-check on {cfg.regulator}
            </a>
        </div>
    );
}

function Row({ Icon, label, children, testid }) {
    return (
        <div data-testid={testid} className="flex items-start gap-2.5 text-sm">
            <Icon className="w-4 h-4 text-aduku-navy/55 mt-0.5 shrink-0" />
            <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wider font-bold text-aduku-navy/55">{label}</div>
                <div className="text-aduku-navy font-semibold mt-0.5">{children}</div>
            </div>
        </div>
    );
}

function Chips({ items, testid }) {
    if (!items || !items.length) return null;
    return (
        <div className="flex flex-wrap gap-1.5" data-testid={testid}>
            {items.map((s) => (
                <span key={s} className="px-2.5 py-1 rounded-full bg-aduku-yellow/25 border border-aduku-yellow/50 text-aduku-navy text-xs font-semibold">{s}</span>
            ))}
        </div>
    );
}

function asArray(v) {
    return Array.isArray(v) ? v.filter(Boolean) : [];
}

/**
 * Pretty card for service-category listing metadata. Renders nothing if
 * the listing isn't one of the new service categories.
 */
export default function ServicesMetaDisplay({ listing }) {
    const meta = listing?.meta || {};
    const cat = listing?.category;
    if (!isServiceCategory(cat)) return null;

    if (cat === "event-management") {
        const types = asArray(meta.event_types);
        const days = asArray(meta.availability_days);
        const hasAny = types.length || meta.capacity || meta.price_unit || meta.packages || days.length || meta.on_site;
        if (!hasAny) return null;
        return (
            <section data-testid="event-mgmt-display" className="bg-white border border-aduku-border rounded-xl p-5 mt-4">
                <div className="flex items-center gap-2 mb-3">
                    <PartyPopper className="w-4 h-4 text-aduku-navy" />
                    <h3 className="font-display font-bold text-lg text-aduku-navy">Event service overview</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    {meta.capacity && <Row Icon={Users} label="Max guests" testid="ev-capacity">{meta.capacity}</Row>}
                    {meta.price_unit && <Row Icon={IndianRupee} label="Pricing" testid="ev-price-unit">{meta.price_unit}</Row>}
                    {meta.on_site && <Row Icon={MapPin} label="On-site" testid="ev-onsite">Yes — works at your venue</Row>}
                    {days.length > 0 && <Row Icon={Calendar} label="Days" testid="ev-days">{days.join(" · ")}</Row>}
                </div>
                {types.length > 0 && (
                    <div className="mt-4">
                        <div className="text-[11px] uppercase tracking-wider font-bold text-aduku-navy/55 mb-1.5">Event types handled</div>
                        <Chips items={types} testid="ev-types-chips" />
                    </div>
                )}
                {meta.packages && (
                    <div className="mt-4">
                        <div className="text-[11px] uppercase tracking-wider font-bold text-aduku-navy/55 mb-1.5">Packages / inclusions</div>
                        <p className="text-sm text-aduku-navy/85 whitespace-pre-wrap" data-testid="ev-packages">{meta.packages}</p>
                    </div>
                )}
            </section>
        );
    }

    if (cat === "groceries") {
        const modes = asArray(meta.payment_modes);
        const hasAny = meta.shop_type || meta.min_order_value || meta.delivery_available || modes.length || meta.operating_hours;
        if (!hasAny) return null;
        return (
            <section data-testid="groceries-display" className="bg-white border border-aduku-border rounded-xl p-5 mt-4">
                <div className="flex items-center gap-2 mb-3">
                    <ShoppingBasket className="w-4 h-4 text-aduku-navy" />
                    <h3 className="font-display font-bold text-lg text-aduku-navy">Grocery store overview</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    {meta.shop_type && <Row Icon={ShoppingBasket} label="Store type" testid="gr-shop-type">{meta.shop_type}</Row>}
                    {meta.min_order_value && <Row Icon={IndianRupee} label="Min order" testid="gr-min-order">₹{meta.min_order_value}</Row>}
                    {meta.delivery_available && (
                        <Row Icon={Truck} label="Home delivery" testid="gr-delivery">
                            {meta.delivery_radius_km ? `Yes — within ${meta.delivery_radius_km} km` : "Yes"}
                        </Row>
                    )}
                    {meta.operating_hours && <Row Icon={Clock} label="Open" testid="gr-hours">{meta.operating_hours}</Row>}
                </div>
                {modes.length > 0 && (
                    <div className="mt-4">
                        <div className="text-[11px] uppercase tracking-wider font-bold text-aduku-navy/55 mb-1.5">Accepted payments</div>
                        <Chips items={modes} testid="gr-modes-chips" />
                    </div>
                )}
            </section>
        );
    }

    if (cat === "fruits-vegetables") {
        const hasAny = meta.produce_type || meta.sale_unit || meta.items || meta.trade_type || meta.min_bulk_qty || meta.organic;
        if (!hasAny) return null;
        return (
            <section data-testid="fruits-veg-display" className="bg-white border border-aduku-border rounded-xl p-5 mt-4">
                <div className="flex items-center gap-2 mb-3">
                    <Apple className="w-4 h-4 text-aduku-navy" />
                    <h3 className="font-display font-bold text-lg text-aduku-navy">Produce overview</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    {meta.produce_type && <Row Icon={Apple} label="Produce" testid="fv-type">{meta.produce_type}</Row>}
                    {meta.sale_unit && <Row Icon={Scale} label="Sale unit" testid="fv-unit">{meta.sale_unit}</Row>}
                    {meta.trade_type && <Row Icon={Package} label="Trade" testid="fv-trade">{meta.trade_type}</Row>}
                    {meta.min_bulk_qty && <Row Icon={Package} label="Min bulk" testid="fv-min-bulk">{meta.min_bulk_qty} kg</Row>}
                    {meta.organic && <Row Icon={Leaf} label="Organic" testid="fv-organic">Pesticide-free / certified organic</Row>}
                </div>
                {meta.items && (
                    <div className="mt-4">
                        <div className="text-[11px] uppercase tracking-wider font-bold text-aduku-navy/55 mb-1.5">Items available</div>
                        <p className="text-sm text-aduku-navy/85 whitespace-pre-wrap" data-testid="fv-items">{meta.items}</p>
                    </div>
                )}
            </section>
        );
    }

    if (cat === "home-maid") {
        const days = asArray(meta.available_days);
        const hasAny =
            meta.service_type || meta.rate_unit || meta.experience_years ||
            meta.languages || meta.gender_preference || days.length ||
            meta.working_hours || meta.live_in;
        if (!hasAny) return null;
        return (
            <section data-testid="home-maid-display" className="bg-white border border-aduku-border rounded-xl p-5 mt-4">
                <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-aduku-navy" />
                    <h3 className="font-display font-bold text-lg text-aduku-navy">Home maid service</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    {meta.service_type && <Row Icon={BadgeCheck} label="Service" testid="hm-service-type">{meta.service_type}</Row>}
                    {meta.rate_unit && <Row Icon={IndianRupee} label="Rate" testid="hm-rate">{meta.rate_unit}</Row>}
                    {meta.experience_years && <Row Icon={Clock} label="Experience" testid="hm-experience">{meta.experience_years} yrs</Row>}
                    {meta.languages && <Row Icon={Globe} label="Languages" testid="hm-languages">{meta.languages}</Row>}
                    {meta.gender_preference && <Row Icon={UserCheck} label="Gender" testid="hm-gender">{meta.gender_preference}</Row>}
                    {meta.working_hours && <Row Icon={Clock} label="Hours" testid="hm-hours">{meta.working_hours}</Row>}
                    {days.length > 0 && <Row Icon={Calendar} label="Days" testid="hm-days">{days.join(" · ")}</Row>}
                    {meta.live_in && <Row Icon={MapPin} label="Live-in" testid="hm-live-in">Available</Row>}
                </div>
            </section>
        );
    }

    if (cat === "mutual-fund-distributor") {
        const products = asArray(meta.product_types);
        const hasAny = meta.arn || meta.euin || products.length || meta.amc_partners || meta.advisory_mode || meta.experience_years || meta.languages || meta.free_consultation;
        if (!hasAny) return null;
        return (
            <section data-testid="mf-distributor-display" className="bg-white border border-aduku-border rounded-xl p-5 mt-4">
                <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="w-4 h-4 text-aduku-navy" />
                    <h3 className="font-display font-bold text-lg text-aduku-navy">Mutual Fund Distributor</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    {meta.arn && <Row Icon={FileText} label="ARN" testid="mf-arn"><span className="font-mono tracking-wider">{meta.arn}</span></Row>}
                    {meta.euin && <Row Icon={FileText} label="EUIN" testid="mf-euin"><span className="font-mono tracking-wider">{meta.euin}</span></Row>}
                    {meta.advisory_mode && <Row Icon={Briefcase} label="Advisory" testid="mf-advisory">{meta.advisory_mode}</Row>}
                    {meta.experience_years && <Row Icon={Clock} label="Experience" testid="mf-experience">{meta.experience_years} yrs</Row>}
                    {meta.languages && <Row Icon={Globe} label="Languages" testid="mf-languages">{meta.languages}</Row>}
                    {meta.free_consultation && <Row Icon={BadgeCheck} label="Consult" testid="mf-free-consult">First consultation included</Row>}
                </div>
                {products.length > 0 && (
                    <div className="mt-4">
                        <div className="text-[11px] uppercase tracking-wider font-bold text-aduku-navy/55 mb-1.5">Products advised on</div>
                        <Chips items={products} testid="mf-product-chips" />
                    </div>
                )}
                {meta.amc_partners && (
                    <div className="mt-4">
                        <div className="text-[11px] uppercase tracking-wider font-bold text-aduku-navy/55 mb-1.5">AMC partners</div>
                        <p className="text-sm text-aduku-navy/85" data-testid="mf-amcs">{meta.amc_partners}</p>
                    </div>
                )}
                <p className="mt-4 text-[11px] text-aduku-navy/55">Mutual fund investments are subject to market risks. Read scheme-related documents carefully before investing.</p>
                <VerificationStrip listing={listing} />
            </section>
        );
    }

    if (cat === "insurance-distributor") {
        const policies = asArray(meta.policy_types);
        const hasAny = meta.irdai_license || meta.distributor_type || policies.length || meta.insurer_partners || meta.advisory_mode || meta.experience_years || meta.languages || meta.claim_assistance || meta.free_consultation;
        if (!hasAny) return null;
        return (
            <section data-testid="insurance-distributor-display" className="bg-white border border-aduku-border rounded-xl p-5 mt-4">
                <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck className="w-4 h-4 text-aduku-navy" />
                    <h3 className="font-display font-bold text-lg text-aduku-navy">Insurance Distributor</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    {meta.irdai_license && <Row Icon={FileText} label="License" testid="ins-license"><span className="font-mono tracking-wider">{meta.irdai_license}</span></Row>}
                    {meta.distributor_type && <Row Icon={Briefcase} label="Type" testid="ins-type">{meta.distributor_type}</Row>}
                    {meta.advisory_mode && <Row Icon={Briefcase} label="Advisory" testid="ins-advisory">{meta.advisory_mode}</Row>}
                    {meta.experience_years && <Row Icon={Clock} label="Experience" testid="ins-experience">{meta.experience_years} yrs</Row>}
                    {meta.languages && <Row Icon={Globe} label="Languages" testid="ins-languages">{meta.languages}</Row>}
                    {meta.claim_assistance && <Row Icon={BadgeCheck} label="Claim help" testid="ins-claim-help">Claim assistance included</Row>}
                    {meta.free_consultation && <Row Icon={BadgeCheck} label="Consult" testid="ins-free-consult">First consultation included</Row>}
                </div>
                {policies.length > 0 && (
                    <div className="mt-4">
                        <div className="text-[11px] uppercase tracking-wider font-bold text-aduku-navy/55 mb-1.5">Policies offered</div>
                        <Chips items={policies} testid="ins-policy-chips" />
                    </div>
                )}
                {meta.insurer_partners && (
                    <div className="mt-4">
                        <div className="text-[11px] uppercase tracking-wider font-bold text-aduku-navy/55 mb-1.5">Insurer partners</div>
                        <p className="text-sm text-aduku-navy/85" data-testid="ins-partners">{meta.insurer_partners}</p>
                    </div>
                )}
                <p className="mt-4 text-[11px] text-aduku-navy/55">Insurance is a subject matter of solicitation. Premium and benefits vary by insurer and risk profile.</p>
                <VerificationStrip listing={listing} />
            </section>
        );
    }

    return null;
}
